import { Box, useMediaQuery, useTheme } from "@mui/material";
import { useEffect, useState } from "react";

import Conversation from "0.Mine/0.Components/CRM/Omnichat/Conversation";
import Inbox from "0.Mine/0.Components/CRM/Omnichat/Inbox";
import SearchBar from "0.Mine/0.Components/CRM/Omnichat/Sidebar";
import { useOmniChat } from "0.Mine/1.Hooks/CRM/omnichat";
import { UseMetricool } from "0.Mine/1.Hooks/social/metricool";
import {
  MapEmailToConversation,
  MapFacebookToConversation,
  MapFacebookToMessages,
  MapGMBReviewToConversation,
  MapInstagramToConversation,
  MapWhatsappToConversation,
  mapWhatsappToMessages,
} from "0.Mine/1.Hooks/utils/omnichat";
import { useBusinessContext } from "0.Mine/2.Context/businessContext";
import { SocialMediaIdentifier } from "0.Mine/3.Interfaces/social";
import ArgonBox from "components/ArgonBox";
import Footer from "examples/Footer";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import BusinessNavbar from "examples/Navbars/DashboardNavbar/businessNavbar";

import { mockContacts, mockConversations } from "./data/MockConvos";

const PRESENTATION_MODE = false;
const TOGGLEEMAILS = false;

function CRMAnswer() {
  return (
    <DashboardLayout>
      <CRMAnswerWithContext />
    </DashboardLayout>
  );
}

function CRMAnswerWithContext() {
  const isTablet = useMediaQuery("(max-width:1280px)");
  const [activeConversation, setActiveConversation] = useState(null);
  const [activeFilter, setActiveFilter] = useState("all");
  const [showContactInfo, setShowContactInfo] = useState(!isTablet);
  const { session } = useBusinessContext();
  const {
    retrieveAllEmails,
    retrieveConversation,
    retrieveMetaConversation,
    retrieveMetaConversations,
    retrieveWhatsappMessages,
    retrieveWhatsappConversation,
  } = useOmniChat();
  const [conversations, setConversations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [recipientId, setRecipientId] = useState(null);

  const [stars, setStars] = useState(null);

  //METRICOOL APIS
  const { getGMBReviews, getDM } = UseMetricool();

  useEffect(() => {
    const fetchData = async () => {
      // If presentation mode is active, use mock data and skip API calls
      if (PRESENTATION_MODE) {
        console.log("Using mock data for presentation");
        setConversations(mockConversations);
        setIsLoading(false);
        return;
      }

      if (session && session.selectedBusiness) {
        setIsLoading(true);

        // Initialize conversation arrays - one per source to handle partial failures
        const emailConversations = [];
        const facebookConversations = [];
        const gmbConversations = [];
        const instagramConversations = [];
        const whatsappConversations = [];

        try {
          // Use Promise.allSettled instead of Promise.all to handle partial failures
          const fetchPromises = [];

          // Email fetch promise
          if (TOGGLEEMAILS) {
            fetchPromises.push(
              retrieveAllEmails(session.selectedBusiness.id)
                .then((emailResponse) => {
                  if (emailResponse && Array.isArray(emailResponse)) {
                    const mapped = MapEmailToConversation(emailResponse).map((conv, index) => ({
                      ...conv,
                      id: conv.id || `email-${index}-${Date.now()}`,
                    }));
                    emailConversations.push(...mapped);
                  }
                })
                .catch((error) => {
                  console.error("Error fetching emails:", error);
                })
            );
          }

          // Facebook fetch promise
          fetchPromises.push(
            retrieveMetaConversations(session.selectedBusiness.id, SocialMediaIdentifier.FACEBOOK)
              .then((facebookResponse) => {
                if (facebookResponse) {
                  const mapped = MapFacebookToConversation(facebookResponse).map((conv, index) => ({
                    ...conv,
                    id: conv.id || `facebook-${index}-${Date.now()}`,
                  }));
                  facebookConversations.push(...mapped);
                }
              })
              .catch((error) => {
                console.error("Error fetching Facebook messages:", error);
              })
          );

          // GMB fetch promise
          fetchPromises.push(
            getGMBReviews(session.selectedBusiness.id)
              .then((response) => {
                if (response) {
                  const mapped = MapGMBReviewToConversation(response).map((conv, index) => ({
                    ...conv,
                    id: conv.id || `gmb-${index}-${Date.now()}`,
                  }));
                  gmbConversations.push(...mapped);
                }
              })
              .catch((error) => {
                console.error("Error fetching GMB messages:", error);
              })
          );

          // Instagram fetch promise
          fetchPromises.push(
            getDM(session.selectedBusiness.id)
              .then((instagramResponse) => {
                if (instagramResponse) {
                  const mapped = MapInstagramToConversation(instagramResponse).map(
                    (conv, index) => ({
                      ...conv,
                      id: conv.id || `instagram-${index}-${Date.now()}`,
                    })
                  );
                  instagramConversations.push(...mapped);
                }
              })
              .catch((error) => {
                console.error("Error fetching Instagram messages:", error);
              })
          );

          // WhatsApp handling - first check cache
          const cachedConversations = getWhatsappConversations(session);
          const hasCachedData = cachedConversations && cachedConversations.length > 0;
          const needsFreshData = !hasCachedData || checkWhatsappRefreshFlag(session);

          if (needsFreshData) {
            fetchPromises.push(
              retrieveWhatsappMessages(session.selectedBusiness.id)
                .then((whatsappResponse) => {
                  if (whatsappResponse) {
                    const mapped = MapWhatsappToConversation(whatsappResponse).map(
                      (conv, index) => ({
                        ...conv,
                        id: conv.id || `whatsapp-${index}-${Date.now()}`,
                      })
                    );
                    whatsappConversations.push(...mapped);

                    // Save to cache
                    saveWhatsappConversations(session, mapped);
                    setWhatsappRefreshFlag(session);
                  }
                })
                .catch((error) => {
                  console.error("Error fetching WhatsApp messages:", error);

                  // Use cached data if available, even on error
                  if (hasCachedData) {
                    whatsappConversations.push(...cachedConversations);
                  }
                })
            );
          } else {
            // Use cached data
            whatsappConversations.push(...cachedConversations);
            fetchPromises.push(Promise.resolve()); // Keep promise structure
          }

          // Wait for all promises to complete (success or failure)
          await Promise.allSettled(fetchPromises);

          // Combine all conversations, even partial results
          const allConversations = [
            ...emailConversations,
            ...facebookConversations,
            ...gmbConversations,
            ...instagramConversations,
            ...whatsappConversations,
          ];

          // Log what sources provided data
          console.log("Conversation sources loaded:", {
            email: emailConversations.length,
            facebook: facebookConversations.length,
            gmb: gmbConversations.length,
            instagram: instagramConversations.length,
            whatsapp: whatsappConversations.length,
            total: allConversations.length,
          });

          // Update state with whatever conversations we were able to get
          setConversations(allConversations);
        } catch (error) {
          console.error("Error in fetchData:", error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchData();
  }, [session]);

  const triggerConversation = async (conversationId) => {
    setStars(null);
    setActiveConversation(conversationId);
    if (conversations && conversations.length > 0) {
      const conversation = conversations.find((conv) => conv.id === conversationId);
      console.log(conversation);
      if (conversation && conversation.contact && conversation.contact.recipient) {
        setRecipientId(conversation.contact.recipient);
      }
      if (conversation.stars) {
        setStars(conversation.stars);
      }
    }

    // Skip API calls in presentation mode - the mock conversations already have messages
    if (PRESENTATION_MODE) return;

    if (conversations && conversations.length > 0) {
      const conversation = conversations.find((conv) => conv.id === conversationId);
      if (conversation) {
        console.log("Conversation found:", conversation);

        // Create a copy of conversations array to modify
        const updatedConversations = [...conversations];
        const conversationIndex = updatedConversations.findIndex(
          (conv) => conv.id === conversationId
        );

        if (conversation.channel === "email") {
          try {
            const request = await retrieveConversation(
              session.selectedBusiness.id,
              conversation.id
            );
            if (request) {
              console.log("Retrieved email conversation:", request);
              // Update with email messages if needed
            }
          } catch (error) {
            console.error("Error retrieving conversation:", error);
          }
        } else if (conversation.channel === "facebook") {
          try {
            const request = await retrieveMetaConversation(
              session.selectedBusiness.id,
              SocialMediaIdentifier.FACEBOOK,
              conversation.id
            );
            if (request) {
              console.log("Retrieved conversation:", request);
              // Map Facebook messages and update the conversation in the array
              const messagesData = MapFacebookToMessages(request.conversation);

              // Update the conversation in our state with the retrieved messages
              if (conversationIndex !== -1 && messagesData) {
                updatedConversations[conversationIndex] = {
                  ...updatedConversations[conversationIndex],
                  messages: messagesData.messages, // Add the messages to the existing conversation
                };

                // Update state with the new conversations array
                setConversations(updatedConversations);
              }
            }
          } catch (error) {
            console.error("Error retrieving conversation:", error);
          }
        } else if (conversation.channel === "whatsapp") {
          try {
            const request = await retrieveWhatsappConversation(
              session.selectedBusiness.id,
              conversation.id
            );
            if (request) {
              console.log("Retrieved WhatsApp conversation:", request);
              // Map WhatsApp messages and update the conversation in the array
              const messagesData = mapWhatsappToMessages(request);

              if (messagesData) {
                // Update the conversation in our state with the retrieved messages
                if (conversationIndex !== -1) {
                  updatedConversations[conversationIndex] = {
                    ...updatedConversations[conversationIndex],
                    messages: messagesData,
                  };

                  // Update state with the new conversations array
                  setConversations(updatedConversations);
                }
              }
            }
          } catch (error) {
            console.error("Error retrieving conversation:", error);
          }
        }
      }
    }
  };

  const addMessage = (messageText) => {
    if (activeConversation && conversations) {
      const updatedConversations = [...conversations];
      const conversationIndex = updatedConversations.findIndex(
        (conv) => conv.id === activeConversation
      );

      if (conversationIndex !== -1) {
        // Create a proper message object
        const newMessage = {
          text: messageText,
          time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
          isOutgoing: true,
        };

        updatedConversations[conversationIndex].messages.push(newMessage);
        setRecipientId(updatedConversations[conversationIndex].contact.recipient);
        setConversations(updatedConversations);
      }
    }
  };

  const filteredConversations = !conversations
    ? []
    : activeFilter === "all"
    ? conversations
    : conversations.filter((conv) => conv.channel === activeFilter);

  // Get details for active conversation - with safety check
  const conversationDetails =
    activeConversation && conversations
      ? conversations.find((conv) => conv.id === activeConversation)
      : null;

  // Get contact details for active conversation - with safety check
  const contactDetails = conversationDetails
    ? {
        id: conversationDetails.contactId || conversationDetails.id,
        name: conversationDetails.contact?.name || "Unknown",
        avatar: conversationDetails.contact?.avatar || "",
        // Add any other contact fields needed
      }
    : null;

  return (
    <>
      <BusinessNavbar />
      <ArgonBox
        py={isTablet ? 2 : 4}
        px={isTablet ? 2 : 3}
        sx={{
          height: "calc(100vh - 170px)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Search bar at the top */}
        <SearchBar activeFilter={activeFilter} setActiveFilter={setActiveFilter} />

        {/* Inbox and conversation area below */}
        <Box
          display="flex"
          flexGrow={1}
          overflow="hidden"
          mt={2}
          sx={{
            border: "1px solid #E5E7EB",
            borderRadius: 1,
            height: "calc(100% - 60px)", // Adjust based on search bar height
          }}
        >
          {/* Inbox component - always 40% width */}
          <Box
            sx={{
              width: "40%",
              minWidth: isTablet ? "280px" : "300px",
              height: "100%",
              overflow: "hidden",
              borderRight: "1px solid #E5E7EB",
            }}
          >
            <Inbox
              conversations={filteredConversations}
              activeConversation={activeConversation}
              setActiveConversation={triggerConversation}
              isLoading={isLoading}
            />
          </Box>

          {/* Conversation area - always 60% width */}
          <Box
            sx={{
              width: "60%",
              display: "flex",
              flexGrow: 1,
              overflow: "hidden",
            }}
          >
            {activeConversation ? (
              <Conversation
                conversation={conversationDetails}
                contact={contactDetails}
                showContactInfo={showContactInfo}
                toggleContactInfo={() => setShowContactInfo(!showContactInfo)}
                addMessage={addMessage}
                conversationId={activeConversation}
                recipient={recipientId}
                stars={stars}
              />
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexGrow: 1,
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "grey.100",
                  color: "text.secondary",
                }}
              >
                Seleziona una conversazione per visualizzarla
              </Box>
            )}
          </Box>
        </Box>
      </ArgonBox>
      <Footer />
    </>
  );
}

// Helper functions for WhatsApp refresh flag and data cache
const checkWhatsappRefreshFlag = (session) => {
  // Use business ID in the key to have separate flags per business
  const businessId = session?.selectedBusiness?.id;
  if (!businessId) return true;

  const whatsappLastRefresh = localStorage.getItem("whatsapp_last_refresh_" + businessId);

  if (!whatsappLastRefresh) {
    console.log("No refresh timestamp found for business", businessId);
    return true; // No timestamp found, allow refresh
  }

  const lastRefreshTime = new Date(parseInt(whatsappLastRefresh));
  const currentTime = new Date();
  const twoHoursInMs = 2 * 60 * 60 * 1000; // 2 hours in milliseconds

  // Calculate time difference
  const timeDiff = currentTime - lastRefreshTime;
  const isExpired = timeDiff > twoHoursInMs;

  console.log(
    "WhatsApp cache status:",
    isExpired ? "EXPIRED" : "VALID",
    "- Last refresh:",
    lastRefreshTime.toLocaleString(),
    "- Time elapsed:",
    Math.round(timeDiff / 60000),
    "minutes"
  );

  // Return true if more than 2 hours have passed since last refresh
  return isExpired;
};

const setWhatsappRefreshFlag = (session) => {
  const businessId = session?.selectedBusiness?.id;
  if (!businessId) return;

  const currentTimestamp = new Date().getTime();
  localStorage.setItem("whatsapp_last_refresh_" + businessId, currentTimestamp.toString());
  console.log("Updated WhatsApp refresh timestamp for business", businessId);
};

const saveWhatsappConversations = (session, conversations) => {
  try {
    // Make sure we have data to save
    if (!conversations || conversations.length === 0) {
      console.warn("No WhatsApp conversations to save");
      return;
    }

    // Ensure data is serializable by handling circular references
    const sanitizedData = JSON.parse(JSON.stringify(conversations));
    const serializedData = JSON.stringify(sanitizedData);

    // Save to localStorage with proper key
    localStorage.setItem("whatsapp_conversations_" + session.selectedBusiness.id, serializedData);
    console.log("Saved WhatsApp conversations to localStorage", conversations.length);
  } catch (error) {
    console.error("Error saving WhatsApp conversations to localStorage:", error);
  }
};

const getWhatsappConversations = (session) => {
  try {
    // Get from localStorage with proper key
    const businessId = session?.selectedBusiness?.id;
    if (!businessId) return [];

    const serializedData = localStorage.getItem("whatsapp_conversations_" + businessId);
    if (!serializedData) {
      console.log("No cached WhatsApp conversations found for business", businessId);
      return [];
    }

    const data = JSON.parse(serializedData);
    console.log("Retrieved WhatsApp conversations from localStorage", data.length);
    return data;
  } catch (error) {
    console.error("Error retrieving WhatsApp conversations from localStorage:", error);
    return [];
  }
};

export default CRMAnswer;
